<template>
  <Dialog
    v-model:visible="visibleProxy"
    modal
    appendTo="body"
    :baseZIndex="1200"
    :closable="!saving"
    :dismissableMask="!saving"
    :draggable="false"
    :style="{ width: '28rem' }"
    :breakpoints="{ '960px': '90vw', '575px': '98vw' }"
    :pt="crcFormDialogPt"
    @hide="onClose"
  >
    <template #header>
      <div class="crc-form-dialog-header">
        <div class="crc-form-dialog-header__icon bg-emerald-50 dark:bg-emerald-500/15 text-emerald-600 dark:text-emerald-400">
          <i class="pi pi-map-marker text-base" />
        </div>
        <div class="crc-form-dialog-header__text">
          <div class="crc-form-dialog-header__title">
            Edit Location
          </div>
        </div>
      </div>
    </template>

    <form @submit.prevent="handleSubmit">
      <section class="crc-form-dialog-section">
        <div class="crc-form-dialog-fields">
          <FloatLabel variant="on" class="w-full">
            <InputText
              id="location-edit-name"
              v-model="draft.name"
              :disabled="saving"
              maxlength="160"
              autocomplete="off"
              :invalid="submitted && !draft.name?.trim()"
              class="w-full rounded-md"
            />
            <label for="location-edit-name" class="text-sm font-medium">
              Name <span class="text-red-500">*</span>
            </label>
          </FloatLabel>
          <small v-if="submitted && !draft.name?.trim()" class="text-xs text-red-500 pl-1">
            Please enter a location name.
          </small>

          <FloatLabel variant="on" class="w-full">
            <Select
              inputId="location-edit-type"
              v-model="draft.type"
              :options="locationTypeOptions"
              optionLabel="label"
              optionValue="value"
              :disabled="saving"
              class="w-full"
              inputClass="w-full rounded-md"
            />
            <label for="location-edit-type" class="text-sm font-medium">Type</label>
          </FloatLabel>
        </div>
      </section>

      <section class="crc-form-dialog-section">
        <div class="crc-form-dialog-section__label">
          Address
        </div>
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FloatLabel variant="on" class="w-full">
            <InputText
              id="location-edit-city"
              v-model="draft.city"
              :disabled="saving"
              maxlength="100"
              class="w-full rounded-md"
            />
            <label for="location-edit-city" class="text-sm font-medium">City</label>
          </FloatLabel>
          <FloatLabel variant="on" class="w-full">
            <InputText
              id="location-edit-state"
              v-model="draft.state"
              :disabled="saving"
              maxlength="80"
              class="w-full rounded-md"
            />
            <label for="location-edit-state" class="text-sm font-medium">State</label>
          </FloatLabel>
          <FloatLabel variant="on" class="w-full sm:col-span-2">
            <InputText
              id="location-edit-country"
              v-model="draft.country"
              :disabled="saving"
              maxlength="80"
              class="w-full rounded-md"
            />
            <label for="location-edit-country" class="text-sm font-medium">Country</label>
          </FloatLabel>
          <FloatLabel variant="on" class="w-full sm:col-span-2">
            <Textarea
              id="location-edit-address"
              v-model="draft.address"
              :disabled="saving"
              rows="2"
              maxlength="1000"
              class="w-full rounded-md"
            />
            <label for="location-edit-address" class="text-sm font-medium">Street address</label>
          </FloatLabel>
        </div>
      </section>

      <section class="crc-form-dialog-section">
        <div class="crc-form-dialog-status">
          <div>
            <div class="crc-form-dialog-status__title">Active</div>
            <div class="crc-form-dialog-status__hint">
              Inactive locations are hidden from employee profile options.
            </div>
          </div>
          <ToggleSwitch v-model="draft.is_active" :disabled="saving" />
        </div>
      </section>

      <div class="crc-form-dialog-footer">
        <div class="crc-form-dialog-footer__actions">
          <Button
            type="button"
            label="Cancel"
            severity="secondary"
            text
            :disabled="saving"
            @click="visibleProxy = false"
          />
          <Button type="submit" label="Save" icon="pi pi-check" :loading="saving" />
        </div>
      </div>
    </form>
  </Dialog>
</template>

<script setup>
import { computed, reactive, ref } from 'vue';
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import Textarea from 'primevue/textarea';
import Select from 'primevue/select';
import FloatLabel from 'primevue/floatlabel';
import ToggleSwitch from 'primevue/toggleswitch';
import { crcFormDialogPt } from '@/components/common/dialog/crcDialogPt.js';
import { LOCATION_TYPE_OPTIONS } from '@/constants/orgReference';

defineOptions({ name: 'LocationEditModal' });

const props = defineProps({
  visible: {
    type: Boolean,
    default: false,
  },
  saving: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:visible', 'save']);

const locationTypeOptions = LOCATION_TYPE_OPTIONS;
const submitted = ref(false);
const addressLoaded = ref(false);

const draft = reactive({
  id: null,
  name: '',
  type: 'office',
  country: '',
  state: '',
  city: '',
  address: '',
  is_active: true,
});

const visibleProxy = computed({
  get: () => props.visible,
  set: (value) => emit('update:visible', value),
});

const resetDraft = () => {
  draft.id = null;
  draft.name = '';
  draft.type = 'office';
  draft.country = '';
  draft.state = '';
  draft.city = '';
  draft.address = '';
  draft.is_active = true;
  submitted.value = false;
  addressLoaded.value = false;
};

const fillDraftFromLocation = (location) => {
  draft.id = location?.id ?? null;
  draft.name = location?.name || '';
  draft.type = location?.type || 'office';
  draft.country = location?.country || '';
  draft.state = location?.state || '';
  draft.city = location?.city || '';
  draft.address = location?.address || '';
  draft.is_active = location?.is_active !== false;
  submitted.value = false;
  addressLoaded.value = Object.prototype.hasOwnProperty.call(location || {}, 'address');
};

const onClose = () => {
  if (!props.saving) {
    resetDraft();
  }
};

const handleSubmit = () => {
  submitted.value = true;
  if (!draft.name?.trim() || !draft.id) return;

  emit('save', {
    location_id: draft.id,
    name: draft.name.trim(),
    type: draft.type,
    country: draft.country?.trim() || null,
    state: draft.state?.trim() || null,
    city: draft.city?.trim() || null,
    is_active: draft.is_active ? 1 : 0,
    ...(addressLoaded.value || draft.address?.trim()
      ? { address: draft.address?.trim() || null }
      : {}),
  });
};

defineExpose({
  openEdit: (location) => {
    fillDraftFromLocation(location);
  },
});
</script>
